import { useState, useContext } from "react";
import { Users, UsersRound, Settings, Tag } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Spinner } from "@/components/ui/spinner";
import { EmployeesTab } from "@/components/EmployeesTab";
import { HandoffRulesTab } from "@/components/HandoffRulesTab";
import { TransferCategoriesTab } from "@/components/TransferCategoriesTab";
import { GruposAsesoresTab } from "@/components/GruposAsesoresTab";
import { AssignmentRuleset, Employee } from "../types";
import MainSectionSection from "@/components/MainSectionSection";
import MerchantContext from "../contexts/MerchantContext";

type HandoffData = {
  employees: Employee[];
  assignmentRulesets: AssignmentRuleset[];
};

type TransferCategory = {
  name: string;
  description: string;
};

type Agent = {
  id: string;
  topic?: string;
  config?: {
    agentName?: string;
  };
};

type HandoffManagementProps = {
  handoffData: HandoffData;
  isLoading: boolean;
  merchantType: "rentals" | "development" | null;
  onCreateEmployee: (
    name: string,
    phone: string,
    email: string,
    crmId?: string,
    projectId?: string
  ) => void;
  onUpdateEmployee: (
    employeeId: string,
    updates: { name: string; phone: string; email?: string; crmId?: string; projectId?: string }
  ) => void;
  onToggleEmployeeStatus: (employeeId: string, status: "active" | "inactive") => void;
  restrictEmployeeStatusChanges?: boolean;
  employeeStatusSuperEmails?: string[];
  callerEmail?: string;
  onCreateAssignmentRuleset: (
    name: string,
    ruleset: AssignmentRuleset["ruleset"],
    priority: number,
    ignorePastEmployee?: boolean
  ) => void;
  onUpdateAssignmentRuleset: (
    assignmentRulesetId: string,
    updates: AssignmentRuleset["ruleset"],
    name?: string,
    priority?: number,
    ignorePastEmployee?: boolean
  ) => void;
  onAddEmployeesToAssignmentRuleset: (assignmentRulesetId: string, employeeIds: string[]) => void;
  onRemoveEmployeesFromAssignmentRuleset: (
    assignmentRulesetId: string,
    employeeIds: string[]
  ) => void;
  cityNeighborhoods: { city: string; neighborhoods: string[] }[];
  transferCategories: { name: string; description: string }[];
  branches: string[];
  pipelines?: Array<{ id: string; name: string }>;
  agents?: Agent[];
  isLoadingAgents?: boolean;
  selectedAgentId?: string;
  agentTransferCategories?: TransferCategory[];
  onAgentChange?: (agentId: string) => void;
  isLoadingAgentCategories?: boolean;
  onSaveAgentCategories?: (categories: TransferCategory[]) => void;
  isSavingAgentCategories?: boolean;
};

export const HandoffManagement = (input: HandoffManagementProps) => {
  const { calComIntegrationEnabled } = useContext(MerchantContext);
  const isDevelopment = input.merchantType === "development";
  const [activeTab, setActiveTab] = useState(isDevelopment ? "agents" : "rules");

  return (
    <MainSectionSection gap="24px">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-foreground md:text-2xl">
          Gestión de transferencias
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          {isDevelopment
            ? "Asigná a tus agentes a proyectos."
            : "Definí cuándo la IA transfiere a un asesor y a quién se lo asigna."}
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList
          className={
            isDevelopment
              ? "grid grid-cols-1 sm:grid-cols-2"
              : "grid grid-cols-1 sm:grid-cols-4"
          }
        >
          {!isDevelopment && (
            <TabsTrigger value="rules" className="flex items-center gap-2 text-sm md:text-base">
              <Settings className="size-4" />
              <span>Reglas de Transferencia</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="agents" className="flex items-center gap-2 text-sm md:text-base">
            <Users className="size-4" />
            <span>Agentes Humanos</span>
          </TabsTrigger>
          <TabsTrigger value="grupos" className="flex items-center gap-2 text-sm md:text-base">
            <UsersRound className="size-4" />
            <span>Grupos de asesores</span>
          </TabsTrigger>
          {!isDevelopment && (
            <TabsTrigger value="categories" className="flex items-center gap-2 text-sm md:text-base">
              <Tag className="size-4" />
              <span>Categorias de Transferencia</span>
            </TabsTrigger>
          )}
        </TabsList>

        {!isDevelopment && (
          <TabsContent value="rules">
            <HandoffRulesTab
              assignmentRulesets={input.handoffData.assignmentRulesets}
              employees={input.handoffData.employees}
              onCreateAssignmentRuleset={input.onCreateAssignmentRuleset}
              onUpdateAssignmentRuleset={input.onUpdateAssignmentRuleset}
              onAddEmployeesToAssignmentRuleset={input.onAddEmployeesToAssignmentRuleset}
              onRemoveEmployeesFromAssignmentRuleset={
                input.onRemoveEmployeesFromAssignmentRuleset
              }
              cityNeighborhoods={input.cityNeighborhoods}
              transferCategories={input.transferCategories}
              branches={input.branches}
              pipelines={input.pipelines}
            />
          </TabsContent>
        )}
        <TabsContent value="agents">
          {input.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <Spinner size="xl" />
                <p className="text-gray-600">Cargando empleados...</p>
              </div>
            </div>
          ) : (
            <EmployeesTab
              employees={input.handoffData.employees}
              assignmentRulesets={input.handoffData.assignmentRulesets}
              onCreateEmployee={input.onCreateEmployee}
              onUpdateEmployee={input.onUpdateEmployee}
              onToggleEmployeeStatus={input.onToggleEmployeeStatus}
              merchantType={input.merchantType}
              calComIntegrationEnabled={calComIntegrationEnabled}
              restrictEmployeeStatusChanges={input.restrictEmployeeStatusChanges}
              employeeStatusSuperEmails={input.employeeStatusSuperEmails}
              callerEmail={input.callerEmail}
            />
          )}
        </TabsContent>
        <TabsContent value="grupos">
          <GruposAsesoresTab />
        </TabsContent>
        {!isDevelopment && (
          <TabsContent value="categories">
            <TransferCategoriesTab
              agents={input.agents || []}
              isLoadingAgents={input.isLoadingAgents || false}
              currentCategories={input.agentTransferCategories}
              isLoadingCategories={input.isLoadingAgentCategories || false}
              selectedAgentId={input.selectedAgentId || ""}
              onAgentChange={input.onAgentChange || (() => {})}
              onSaveCategories={input.onSaveAgentCategories || (() => {})}
              isSaving={input.isSavingAgentCategories || false}
            />
          </TabsContent>
        )}
      </Tabs>
    </MainSectionSection>
  );
};
